import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Fonts, Palette } from '../../components/utils';
import { Tooltip } from 'antd';

export const TemperatureInput = ({ defaultValue = 74, value, onSubmit, unitDisplay, unitSuffix, alwaysShow, cssOverride, tooltipTitle }) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const overlayRef = useRef(null);

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSubmit = (newValue) => {
    setInputValue(newValue);

    onSubmit(newValue, (success) => {
      if (!success) {
        setInputValue(value);
      }
    });
  };

  const shouldShowTooltip = window.innerWidth <= 1600;
  const tooltipContent = shouldShowTooltip ? tooltipTitle || unitDisplay : null;

  const inputComponent = (
    <InputWrapper cssOverride={cssOverride}>
      <TransparentInput
        alwaysShow={alwaysShow}
        type="number"
        ref={inputRef}
        value={inputValue}
        onChange={handleChange}
        onBlur={() => {
          if (inputValue !== value) {
            handleSubmit(inputValue);
          }
        }}
      />

      <TextOverlay ref={overlayRef} alwaysShow={alwaysShow}>
        <TextContainer >
          <ValueText>{inputValue}</ValueText>
          <UnitSuffix className="unit-suffix">{unitSuffix}</UnitSuffix>
        </TextContainer>
      </TextOverlay>

      <UnitDisplay>{unitDisplay}</UnitDisplay>
    </InputWrapper>
  );

  return shouldShowTooltip ? (
    <Tooltip title={tooltipContent} placement="top" trigger="focus">
      {inputComponent}
    </Tooltip>
  ) : (
    inputComponent
  );
};

const UnitDisplay = styled.span`
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  color: ${Palette.CONTENT_TERTIARY};
  overflow: ellipsis;
  text-align: right;
  font-weight: 400;
  display: none;
  
  /* Hide on smaller screens (≤ 1600px) */
  @media (max-width: 1600px) {
    display: none !important;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 2rem;
  
  /* Show unit display on focus for larger screens (> 1600px) */
  @media (min-width: 1601px) {
    &:focus-within ${UnitDisplay} {
      display: inline;
    }
  }

  /* Hide unit display on smaller screens */
  @media (max-width: 1600px) {
    &:focus-within ${UnitDisplay} {
      display: none !important;
    }
  }

  &:focus-within .unit-suffix {
    display: none;
  }

  ${({ cssOverride }) => cssOverride};
`;

const TransparentInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  
  color: transparent;
  caret-color: ${Palette.BLACK};
  
  padding: 0.625rem 1.5rem 0.625rem 0;
  
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  
  border-radius: 0.3125rem;
  border: 1px solid transparent;
  
  background-color: inherit;
  outline: none;

    ${({ alwaysShow }) =>
    alwaysShow &&
    `
    border: 1px solid ${Palette.LIGHT_GREY};
  `}
  
  &:hover {
    border: 1px solid ${Palette.LIGHT_GREY};
    padding-left: 0.625rem;
  }

  &:active, &:focus {
    border: 1px solid ${Palette.OCEANIC};
    padding-left: 0.625rem;
  }
  
  z-index: 2;
`;

const TextOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  pointer-events: none;
  
  padding: 0.625rem 1.5rem 0.625rem 0;
  
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  color: ${Palette.BLACK};
  
  z-index: 1;

  ${({ alwaysShow }) =>
    alwaysShow &&
    `
    padding-left: 0.625rem;
  `}

  ${TransparentInput}:hover ~ & {
    padding-left: 0.625rem;
  }
  
  ${TransparentInput}:focus ~ & {
    padding-left: 0.625rem;
  }
`;

const TextContainer = styled.div`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
`;

const ValueText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const UnitSuffix = styled.span`
  margin-left: 1px;
  white-space: pre;
  flex-shrink: 0;
`;
